/**
 * @typedef {Object} ShopifyProduct
 * @property {number} id
 * @property {string} title
 * @property {number} price - The price of the product in cents.
 * @property {ShopifyVariant[]}
 * @property {string} [featured_image]
 * @property {string} [handle]
 * @property {string} [type]
 * @property {string} [vendor]
 *
 * @typedef {Object} ShopifyVariant
 * @property {number} id
 * @property {string} name
 * @property {number} price - The price of the variant in cents.
 * @property {number} [compare_at_price] - The compare at price of the variant in cents.
 * @property {string} sku
 * @property {string} [option1] - The first option of the variant.
 * @property {string} [option2] - The second option of the variant.
 * @property {string} [option3] - The third option of the variant.
 * @property {Object[]} [selling_plan_allocations]
 */

/**
 * @typedef {Object} ProductExceptionObj
 * @property {string} [min_date]
 * @property {string} [max_date]
 * @property {string[]} [available_dates]
 * @property {boolean} [unavailable_shipping]
 * @property {string} [city_exclusive]
 */

/**
 * Represents inventory details from production endpoint.
 * @typedef {Object} InventoryDetails
 * @property {number} inventory_item_id
 * @property {number} location_id
 * @property {number} available
 * @property {string} state
 * @property {string} location
 * @property {number} variant_id
 * @property {number} product_id
 * @property {string} variant_title
 */

/**
 * @typedef {Object} Zone
 * @property {string} id
 * @property {string} name
 * @property {string} description
 * @property {string} state
 * @property {string} color
 * @property {boolean} isPickup
 */

/**
 * @typedef {Object} VariantPrice
 * @property {number|null} price - The variant's price in cents.
 * @property {number|null} compare_at_price - The variant's compare-at price in cents.
 */

/**
 * @typedef {Object} SubscriptionPrices
 * @property {'weekly'|'biweekly'|'monthly'} title - The subscription frequency.
 * @property {VariantPrice} small - Pricing details for the Small / None variant.
 * @property {VariantPrice} big - Pricing details for the Big / None variant.
 */

export default class ProductDaily {
  /**
   * Creates a new ProductDaily instance
   * @param {Object} ProductImagePicker - Image picker class
   * @param {Object} ProductImageMobileCarousel - Mobile carousel class
   */
  constructor(ProductImagePicker, ProductImageMobileCarousel) {
    const customData = window.Shopify?.customData?.productDaily || {}

    // Product data
    /** @type {ShopifyProduct} */
    this.suggestedPairingProduct = customData.suggestedPairingProduct
    /** @type {ShopifyVariant} */
    this.preselectedVariantId = customData.preselectedVariantId
    /** @type {ShopifyProduct} */
    this.product = customData.product
    /** @type {string} */
    this.productCollectionTitle = customData.productCollectionTitle
    /** @type {string} */
    this.giftSetTitle = customData.giftSetTitle
    /** @type {ShopifyVariant[]} */
    this.variants = customData.variants || []
    /** @type {number} */
    this.numberOfVases = customData.numberOfVases
    /** @type {ShopifyVariant} */
    this.defaultVariant = customData.defaultVariant
    /** @type {boolean} */
    this.includeBundle = customData.includeBundle
    /** @type {ShopifyProduct[]} */
    this.currentBundleProducts = customData.currentBundleProducts
    /** @type {number} */
    this.availableVariants = customData.availableVariants
    /** @type {ProductExceptionObj} */
    this.productException = customData.productException
    /** @type {SubscriptionPrices[]} */
    this.artisanSubscriptionPrices = customData.artisanSubscription?.slice(0, 3).map((product, i) => {
      const small = product.variants.find(v => v.title === 'Small / None')
      const big = product.variants.find(v => v.title === 'Big / None')
    
      const titles = ['weekly', 'biweekly', 'monthly']
    
      return {
        title: titles[i],
        small: {
          price: small?.price ?? null,
          compare_at_price: small?.compare_at_price ?? null
        },
        big: {
          price: big?.price ?? null,
          compare_at_price: big?.compare_at_price ?? null
        }
      }
    })
    /** @type {boolean} */
    this.subsWidget = customData.subsWidget
    /** @type {string} */ 
    this.customerEmail = customData.customerEmail
    /** @type {string} */
    this.customerId = customData.customerId

    // State
    /** @type {ShopifyVariant} */
    this.selectedVariant = this.defaultVariant
    this.productId = this.product?.id
    this.productTitle = this.product?.title
    this.selectedSize = ""
    this.selectedVase = ""
    /** @type {Zone|null} */
    this.selectedZone = null
    this.isSuggestedPairing = false
    /** @type {InventoryDetails[]|null} */
    this.soldOutVariants = null
    /** @type {InventoryDetails[]|null} */
    this.soldOutBundles = null
    this.selectedZip = false
    this.isPickup = false
    this.subsSizeBig = false

    // DOM elements
    /** @type {HTMLElement[]} */
    this.sizeOptionsEl = document.getElementsByClassName(this.productId + "-size")
    /** @type {HTMLElement[]} */
    this.vaseSelectorsEl = document.getElementsByClassName("vase-selector")

    // Initialize image picker
    this.productImagePicker = new ProductImagePicker(ProductImageMobileCarousel)

    this.build()
  }

  /**
   * Initialize the component
   */
  async build() {
    this.addEventListeners()

    // Update UI based on variant count
    if ((this.variants.length > 1 && this.numberOfVases < 2) || this.availableVariants > 7) {
      document.getElementById("variants-wrapper")?.classList.remove("xl:border-r-[1px]")
    }

    // Add click handlers for size options
    for (const size of this.sizeOptionsEl) {
      size.addEventListener("click", (event) => {
        // Always allow size selection regardless of current vase selection
        // This ensures users can switch between sizes even if the current vase isn't available for that size
        const newSize = event.currentTarget.id.split("-").slice(1).join("-")
        this.selectedSize = newSize
        this.updateSizeUI()
        this.updateVaseAvailability()

        // If current vase is not valid for this size, select the first valid vase
        if (!this.isValidCombination(this.selectedSize, this.selectedVase)) {
          this.selectFirstValidVase()
        }

        if (this.subsWidget) {
          this.updateSubscriptionPrices()
        }
        this.updateVariant()
      })
    }

    // Remove any existing click handlers from vase selectors
    for (const selector of this.vaseSelectorsEl) {
      const newSelector = selector.cloneNode(true)
      selector.parentNode.replaceChild(newSelector, selector)
    }

    // Re-get the vase selectors after replacing them
    const refreshedVaseSelectors = document.getElementsByClassName("vase-selector")

    // Add click handlers for vase selectors
    for (const selector of refreshedVaseSelectors) {
      selector.addEventListener("click", (event) => {
        // Get the vase ID from the selector element itself
        const vase = selector.id.split("-")[1]

        // Check if this vase is valid for the current size
        if (this.selectedSize && !this.isValidCombination(this.selectedSize, vase)) {
          event.preventDefault()
          event.stopPropagation()
          return false
        }

        this.selectedVase = vase
        this.updateVaseUI()
        this.updateSizeAvailability()
        this.updateVariant()
      })
    }

    // Set default variant
    const preselectedVariant = this.variants.find((e) => e.id === this.preselectedVariantId)
    this.defaultVariant = this.preselectedVariantId && preselectedVariant ? preselectedVariant : this.defaultVariant

    this.selectedSize = this.defaultVariant?.option1 || null
    this.selectedVase = this.defaultVariant?.option2 || null

    // Get product availabilities
    const productAvailabilities = await this.getLocationAvailabilities()
    
    // Filter out truck and untracked inventory placeholder locations
    const filteredProductAvailabilities = productAvailabilities.filter((prod) => prod.location_id !== 67872096415 && prod.location_id !== 67819307167)
    this.soldOutVariants = filteredProductAvailabilities.filter((prod) => prod.product_id === Number(this.productId))
    this.soldOutBundles = filteredProductAvailabilities.filter((prod) => prod.product_id !== Number(this.productId) && prod.state)

    this.soldOutBundlesHandler()

    // Important: Update both size and vase availability BEFORE selecting them
    // This ensures options are disabled immediately on page load
    if (this.selectedVase) {
      this.updateSizeAvailability()
      this.updateVaseUI()
    }

    if (this.selectedSize) {
      this.updateVaseAvailability()
      this.updateSizeUI()
      if (this.subsWidget) {
        this.updateSubscriptionPrices()
      }
    }

    this.updateVariant()
  }

  /**
   * Add event listeners for custom events
   */
  addEventListeners() {
    document.addEventListener("zonedatepicked", async (event) => {
      if (event.detail.zipcode) this.selectedZip = true
      else this.selectedZip = false

      if (event.detail.zone?.color === "pickup") this.isPickup = true
      else this.isPickup = false

      this.productDailyZonePicked(event)
    })

    document.addEventListener("goodpairingselector", (event) => {
      this.isSuggestedPairing = event.detail.isSuggestedPairing
      this.includeBundle = event.detail.includePairing

      this.updateProductInfo(event.detail.pairingPrice, event.detail.pairingTitle)
      this.productImagePicker.displayThumbs(
        this.selectedVariant.id,
        this.product.title,
        this.isSuggestedPairing,
        this.includeBundle,
      )
    })

    if (this.subsWidget) {
      this.initWidgetOptionEvents()
    }
  }

  initWidgetOptionEvents() {
    const subsWidgetBtn = document.getElementById('subs-widget-btn')
    const subsWidget = document.getElementById('subscription-widget')
    const weeklyOpt = subsWidget.querySelector('#subs-weekly-opt')
    const biWeeklyOpt = subsWidget.querySelector('#subs-biweekly-opt')
    const monthlyOpt = subsWidget.querySelector('#subs-monthly-opt')
    const subsInfoBtn = subsWidget.querySelector('#subs-info-btn')
    const startSubBtn = subsWidget.querySelector('#start-sub-btn')

    const dailiesWidgetBtn = document.getElementById('dailies-widget-btn')
    const dailiesWidget = document.getElementById('dailies-widget')
    
    subsWidgetBtn.addEventListener('click', () => {
      this.showWidget(subsWidget, dailiesWidget)
    
      subsWidgetBtn.classList.add('bg-white', 'text-black')
      subsWidgetBtn.classList.remove('bg-gray-50', 'text-gray-500')
      dailiesWidgetBtn.classList.add('bg-gray-50', 'text-gray-500')
      dailiesWidgetBtn.classList.remove('bg-white', 'text-black')
    })
    
    dailiesWidgetBtn.addEventListener('click', () => {
      this.showWidget(dailiesWidget, subsWidget)
    
      dailiesWidgetBtn.classList.add('bg-white', 'text-black')
      dailiesWidgetBtn.classList.remove('bg-gray-50', 'text-gray-500')
      subsWidgetBtn.classList.add('bg-gray-50', 'text-gray-500')
      subsWidgetBtn.classList.remove('bg-white', 'text-black')
    })

    const subOptions = [weeklyOpt, biWeeklyOpt, monthlyOpt]

    subOptions.forEach(option => {
      option.addEventListener('click', () => {
        subOptions.forEach(opt => {
          // Reset card styles
          opt.classList.remove('border-gray-900', 'bg-gray-50')
          opt.classList.add('border-gray-200', 'bg-white')
    
          // Reset bubble
          const bubble = opt.querySelector('.bubble')
          bubble.classList.remove('bg-copper', 'border-copper')
          bubble.classList.add('bg-white', 'border-gray-400')
    
          // Hide checkmark
          const checkmark = opt.querySelector('.checkmark')
          checkmark.classList.remove('block')
          checkmark.classList.add('hidden')
    
          // Uncheck radio
          const input = opt.querySelector('input[type="radio"]')
          input.checked = false
        })
    
        // Activate clicked card
        option.classList.add('border-gray-900', 'bg-gray-50')
        option.classList.remove('border-gray-200', 'bg-white')
    
        const bubble = option.querySelector('.bubble')
        bubble.classList.remove('bg-white', 'border-gray-400')
        bubble.classList.add('bg-copper', 'border-copper')
    
        const checkmark = option.querySelector('.checkmark')
        checkmark.classList.remove('hidden')
        checkmark.classList.add('block')
    
        const input = option.querySelector('input[type="radio"]')
        input.checked = true
        input.dispatchEvent(new Event('change', { bubbles: true }))
      })
    })

    subsInfoBtn.addEventListener('click', function() {
      document.dispatchEvent(new CustomEvent('open-subs-info-modal'));
    });

    startSubBtn.addEventListener('click', () => {
      const activeInput = document.querySelector('input[name="delivery"]:checked')
      let path  = `/collections/artisan-flower-subscription?preselect=${activeInput.value}`

      if (this.subsSizeBig) path += '&big'

      const dataLayerData = {
        event: 'subscription_button',
        product_name: this.product.title,
        product_id: String(this.product.id),
        frequency: activeInput.value
      }

      if (this.customerEmail) {
        dataLayerData.customer_email = this.customerEmail
        dataLayerData.customer_id = this.customerId
      }

      window.dataLayer = window.dataLayer || []
      window.dataLayer.push(dataLayerData)
      
      window.location = path
    })
    
  }

  /**
   * Fades out one widget and fades in another using Tailwind CSS classes.
   *
   * @param {HTMLElement} widgetToShow - The DOM element to fade in and display.
   * @param {HTMLElement} widgetToHide - The DOM element to fade out and hide.
   */
  showWidget(widgetToShow, widgetToHide) {
    // Fade out the currently visible widget
    widgetToHide.classList.remove('opacity-100')
    widgetToHide.classList.add('opacity-0')
  
    // Wait for the fade-out to finish
    setTimeout(() => {
      widgetToHide.classList.add('hidden')
  
      // Then show and fade in the new widget
      widgetToShow.classList.remove('hidden')
      
      // Force a reflow to ensure the opacity transition triggers correctly
      void widgetToShow.offsetWidth
      
      widgetToShow.classList.remove('opacity-0')
      widgetToShow.classList.add('opacity-100')
    }, 300) // match duration-300
  }

  /**
   * Handle zone selection event
   * @param {CustomEvent} event - The zone picked event
   */
  productDailyZonePicked(event) {
    this.selectedZone = event.detail.zone
    this.soldOutBundlesHandler()
    this.checkVariantAvailability()
  }

  /**
   * Update UI for size selection and disable invalid vase combinations
   */
  updateSizeUI() {
    // Existing size selection code remains the same
    for (const sizeElement of this.sizeOptionsEl) {
      if (!sizeElement.dataset) continue

      const nonSelectedSizeClasses = []
      const selectedSizeClasses = ["border-[1px]"]

      // Determine text size classes based on display length
      if (sizeElement.dataset.desktopDisplay?.length > 2 && sizeElement.dataset.mobileDisplay?.length > 2) {
        nonSelectedSizeClasses.push("desktop:text-sm", "text-sm")
        selectedSizeClasses.push("desktop:text-base", "text-base")
      } else if (sizeElement.dataset.desktopDisplay?.length > 2) {
        nonSelectedSizeClasses.push("desktop:text-sm", "text-base")
        selectedSizeClasses.push("desktop:text-base", "text-xl")
      } else if (sizeElement.dataset.mobileDisplay?.length > 2) {
        nonSelectedSizeClasses.push("desktop:text-base", "text-sm")
        selectedSizeClasses.push("desktop:text-xl", "text-base")
      } else {
        nonSelectedSizeClasses.push("desktop:text-base", "text-base")
        selectedSizeClasses.push("desktop:text-xl", "text-xl")
      }

      // Apply appropriate classes
      if (sizeElement.dataset.size !== this.selectedSize) {
        sizeElement.classList.remove(...selectedSizeClasses)
        sizeElement.classList.add(...nonSelectedSizeClasses)
      } else {
        sizeElement.classList.remove(...nonSelectedSizeClasses)
        sizeElement.classList.add(...selectedSizeClasses)
      }
    }
  }

    /**
   * Update prices for subscription widget
   */
  updateSubscriptionPrices() {
    const weeklyPrice = document.getElementById('weekly-price')
    const weeklyComaprePrice = document.getElementById('weekly-compare-price')
    const biweeklyPrice = document.getElementById('biweekly-price')
    const biweeklyComaprePrice = document.getElementById('biweekly-compare-price')
    const monthlyPrice = document.getElementById('monthly-price')
    this.subsSizeBig = this.selectedSize === 'Large' || this.selectedSize === 'Grand' || this.selectedSize.includes('Double')

    if (this.subsSizeBig) {
      weeklyPrice.innerText = `$${(this.artisanSubscriptionPrices[0].big.price / 4 / 100).toFixed(2)}`
      weeklyComaprePrice.innerText = `$${(this.artisanSubscriptionPrices[0].big.compare_at_price / 100).toFixed(2)}`
      biweeklyPrice.innerText = `$${(this.artisanSubscriptionPrices[1].big.price / 2 / 100).toFixed(2)}`
      biweeklyComaprePrice.innerText = `$${(this.artisanSubscriptionPrices[1].big.compare_at_price / 100).toFixed(2)}`
      monthlyPrice.innerText = `$${(this.artisanSubscriptionPrices[2].big.price / 100).toFixed(2)}`
    } else {
      weeklyPrice.innerText = `$${(this.artisanSubscriptionPrices[0].small.price / 4 / 100).toFixed(2)}`
      weeklyComaprePrice.innerText = `$${(this.artisanSubscriptionPrices[0].small.compare_at_price / 100).toFixed(2)}`
      biweeklyPrice.innerText = `$${(this.artisanSubscriptionPrices[1].small.price / 2 / 100).toFixed(2)}`
      biweeklyComaprePrice.innerText = `$${(this.artisanSubscriptionPrices[1].small.compare_at_price / 100).toFixed(2)}`
      monthlyPrice.innerText = `$${(this.artisanSubscriptionPrices[2].small.price / 100).toFixed(2)}`
    }

  }

  /**
   * Update UI for vase selection
   */
  updateVaseUI() {
    const backgrounds = document.getElementsByClassName("vase")

    for (const background of backgrounds) {
      const vase = background.id.split("-")[1]
      if (vase === this.selectedVase) {
        background.classList.add("border-[1px]")
      } else {
        background.classList.remove("border-[1px]", "bg-transparent")
      }
    }
  }

  /**
   * Fetch inventory availability from API
   * @returns {Promise<InventoryDetails[]>} Array of inventory details
   */
  async getLocationAvailabilities() {
    // Build payload of product variants to get inventory availability
    const productVariants = []

    // Add bundle products
    if (this.currentBundleProducts?.length) {
      this.currentBundleProducts.forEach((bundleProduct) => {
        const bundleVariant = bundleProduct.variants[0]
        if (!bundleVariant) {
          console.error("Invalid bundleProduct", bundleProduct)
        } else {
          productVariants.push({
            productId: String(bundleProduct.id),
            variantId: String(bundleVariant.id),
            sku: bundleVariant.sku,
            name: bundleVariant.name,
          })
        }
      })
    }

    // Add product variants
    this.variants.forEach((productVariant) => {
      productVariants.push({
        productId: this.productId,
        variantId: String(productVariant.id),
        sku: productVariant.sku,
        name: productVariant.name,
      })
    })

    const config = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization:
          "Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJkYXRhIjoiY2xpZW50IiwiZXhwIjoxNzI2ODQ5MjI1NTM3LCJpYXQiOjE2NjM3NzcyMjV9.3j5DSp1wxF2hi6rgYckXfgvppXgA-9bQn2aUfZKYUDg",
      },
      mode: "cors",
      body: JSON.stringify({ variants: productVariants }),
    }

    const path = "https://admin.f4d.space/api/shopify/product-inventory-levels"

    try {
      const response = await fetch(path, config)
      const data = await response.json()
      return data.inventory.filter((item) => !item.available || item.available <= 0)
    } catch (error) {
      console.error("Error fetching inventory levels:", error)
      return []
    }
  }

  /**
   * Dispatch event with sold out bundles information
   */
  soldOutBundlesHandler() {
    const soldOutBundlesEvent = new CustomEvent("soldoutbundlesevent", {
      detail: {
        soldOutBundles: this.soldOutBundles,
        selectedZone: this.selectedZone,
      },
    })
    document.dispatchEvent(soldOutBundlesEvent)
  }

  /**
   * Update the selected variant based on size and vase selections
   */
  updateVariant() {
    this.selectedVariant = this.variants.find(
      (variant) => variant.option1 === this.selectedSize && variant.option2 === this.selectedVase,
    )

    this.updateProductInfo()
    this.checkVariantAvailability()

    this.productImagePicker.displayThumbs(
      this.selectedVariant.id,
      this.product.title,
      this.isSuggestedPairing,
      this.includeBundle,
      this.product.featured_image,
    )
  }

  /**
   * Update product information in the UI
   * @param {number} [bundlePrice=0] - Price of the bundle
   * @param {string} [bundleName] - Name of the bundle
   */
  updateProductInfo(bundlePrice = 0, bundleName) {
    let newPrice = this.selectedVariant.price
    const selectedVariantPrice = this.selectedVariant.price
    const prodTitleContainers = document.getElementsByClassName(`${this.productId}-title-container`)

    // Calculate price with bundle if applicable
    if (this.isSuggestedPairing) {
      newPrice = this.includeBundle ? selectedVariantPrice + this.suggestedPairingProduct.price : selectedVariantPrice
      newPrice = String(newPrice).split("")
      newPrice.splice(newPrice.length - 2, 0, ".")
    } else {
      newPrice = this.includeBundle ? selectedVariantPrice + Number(bundlePrice) : selectedVariantPrice
      newPrice = String(newPrice).split("")
      newPrice.splice(newPrice.length - 2, 0, ".")
    }

    // Update UI elements
    for (let i = 0; i < prodTitleContainers.length; i++) {
      const variantPriceDiv = prodTitleContainers[i].querySelector(".selected-variant-price")
      const title = prodTitleContainers[i].querySelector(".prod-title-container")
      const compareAtPriceDiv = prodTitleContainers[i].querySelector(".compare-at-price")

      // Update price
      if (variantPriceDiv) {
        variantPriceDiv.innerText = `$${newPrice.join("")}`
      }

      // Update title
      if (title) {
        if (this.includeBundle) {
          if (this.isSuggestedPairing) {
            title.innerText = this.giftSetTitle?.length ? this.giftSetTitle : `${this.productTitle} Gift Set`
          } else if (bundleName) {
            title.innerText = `${this.productTitle} + ${bundleName}`
          }
        } else {
          title.innerText = `${this.productTitle}`
        }
      }

      // Update compare-at price if available
      if (compareAtPriceDiv) {
        if (this.selectedVariant.compare_at_price) {
          const comparePrice = this.includeBundle
            ? this.selectedVariant.compare_at_price + Number(bundlePrice)
            : this.selectedVariant.compare_at_price
          compareAtPriceDiv.innerText = `$${(comparePrice / 100).toFixed(2)}`
          compareAtPriceDiv.classList.remove("hidden")
          compareAtPriceDiv.classList.add("inline-block")
        } else {
          compareAtPriceDiv.classList.add("hidden")
        }
      }
    }
  }

  /**
   * Check if the product has limited availability
   * @returns {boolean} True if product has limited availability
   */
  handleLimitedAvailability() {
    if (this.soldOutVariants?.find((vari) => vari.variant_id == this.selectedVariant?.id)) {
      return true
    } else if (this.productException?.unavailable_shipping) {
      return true
    } else if (this.productException?.city_exclusive) {
      return true
    }

    return false
  }

  /**
   * Check if the product is sold out in the selected zone
   * @returns {string|null} Error message if sold out, null otherwise
   */
  handleSoldOut() {
    // Don't show sold out message if no zone is selected
    if (!this.selectedZone) return null

    if (
      this.soldOutVariants?.find(
        (vari) => vari.variant_id == this.selectedVariant?.id && this.selectedZone?.state === vari.state,
      )
    ) {
      return "Sorry, we're out of stock of this size in your area!"
    } else if (this.productException?.unavailable_shipping && this.selectedZone?.description.includes("shipping")) {
      return "Sorry, this product is unavailable in your area!"
    }

    return null
  }

  /**
   * Check variant availability and dispatch appropriate event
   */
  checkVariantAvailability() {
    const selectedVariantLimitedAvailability = this.handleLimitedAvailability()
    const selectedVariantSoldOut = this.handleSoldOut()

    if (selectedVariantSoldOut) {
      this.dailiesCardVariantSelectorEventHandler(null, false, selectedVariantSoldOut)
    } else if (selectedVariantLimitedAvailability && !this.selectedZone) {
      this.dailiesCardVariantSelectorEventHandler(this.selectedVariant, true)
    } else {
      this.dailiesCardVariantSelectorEventHandler(this.selectedVariant)
    }
  }

  /**
   * Dispatch variant selected event
   * @param {ShopifyVariant} variant - The selected variant
   * @param {boolean} [limitedAvailability=false] - Whether the variant has limited availability
   * @param {string} [errorMessage] - Error message if variant is unavailable
   */
  dailiesCardVariantSelectorEventHandler(variant, limitedAvailability = false, errorMessage) {
    const variantSelected = new CustomEvent("variantselected", {
      detail: {
        product: this.product,
        productCollection: this.productCollectionTitle,
        variant,
        limitedAvailability,
        soldOutVariants: this.soldOutVariants,
        errorMessage,
      },
    })
    document.dispatchEvent(variantSelected)
  }

  /**
   * Check if a size and vase combination exists as a variant
   * @param {string} size - The size option
   * @param {string} vase - The vase option
   * @returns {boolean} - Whether the combination exists
   */
  isValidCombination(size, vase) {
    return this.variants.some((variant) => variant.option1 === size && variant.option2 === vase)
  }

  /**
   * Get all available sizes for a specific vase
   * @param {string} vase - The vase option
   * @returns {string[]} - Array of available sizes for this vase
   */
  getAvailableSizesForVase(vase) {
    return [...new Set(this.variants.filter((variant) => variant.option2 === vase).map((variant) => variant.option1))]
  }

  /**
   * Get all available vases for a specific size
   * @param {string} size - The size option
   * @returns {string[]} - Array of available vases for this size
   */
  getAvailableVasesForSize(size) {
    return [...new Set(this.variants.filter((variant) => variant.option1 === size).map((variant) => variant.option2))]
  }

  /**
   * Check if a size has any available vase options
   * @param {string} size - The size to check
   * @returns {boolean} - Whether the size has any vase options
   */
  sizeHasVaseOptions(size) {
    return this.getAvailableVasesForSize(size).length > 0
  }

  /**
   * Update vase options based on the selected size
   * Disables vases that don't have a variant with the selected size
   */
  updateVaseAvailability() {
    if (!this.selectedSize) return

    const vaseSelectors = document.getElementsByClassName("vase-selector")

    for (const selector of vaseSelectors) {
      const vase = selector.id.split("-")[1]
      const isValidOption = this.isValidCombination(this.selectedSize, vase)
      const vaseBackground = document.getElementById(`${this.productId}-${vase}-bg`)

      if (!isValidOption) {
        // Disable this vase option with visual indicators
        selector.classList.add("opacity-50", "cursor-not-allowed")

        // Make sure pointer events are disabled at the selector level
        selector.style.pointerEvents = "none"

        if (vaseBackground) {
          vaseBackground.classList.add("opacity-50", "cursor-not-allowed")
          vaseBackground.style.pointerEvents = "none"

          // Add strike-through effect
          if (!vaseBackground.querySelector(".strike-through-line")) {
            const strikeThrough = document.createElement("div")
            strikeThrough.className = "strike-through-line"
            strikeThrough.style.position = "absolute"
            strikeThrough.style.top = "50%"
            strikeThrough.style.left = "0"
            strikeThrough.style.right = "0"
            strikeThrough.style.borderTop = "2px solid #8D694B"
            strikeThrough.style.transform = "rotate(-15deg)"
            strikeThrough.style.zIndex = "10"
            vaseBackground.appendChild(strikeThrough)
          }
        }

        // If this was the selected vase, we need to select a valid one
        if (vase === this.selectedVase) {
          this.selectFirstValidVase()
        }
      } else {
        // Enable this vase option
        selector.classList.remove("opacity-50", "cursor-not-allowed")
        selector.style.pointerEvents = "auto"

        if (vaseBackground) {
          vaseBackground.classList.remove("opacity-50", "cursor-not-allowed")
          vaseBackground.style.pointerEvents = "auto"

          // Remove strike-through line if it exists
          const strikeThrough = vaseBackground.querySelector(".strike-through-line")
          if (strikeThrough) {
            vaseBackground.removeChild(strikeThrough)
          }
        }
      }
    }
  }

  /**
   * Select the first valid vase option for the current size
   */
  selectFirstValidVase() {
    for (const selector of this.vaseSelectorsEl) {
      const vase = selector.id.split("-")[1]
      if (this.isValidCombination(this.selectedSize, vase)) {
        this.selectedVase = vase
        this.updateVaseUI()
        break
      }
    }
  }

  /**
   * Update size options based on the selected vase
   * Disables sizes that don't have a variant with the selected vase
   */
  updateSizeAvailability() {
    if (!this.selectedVase) return

    // Never disable all size options - ensure at least one size is always selectable
    for (const sizeElement of this.sizeOptionsEl) {
      if (!sizeElement.dataset) continue

      const size = sizeElement.dataset.size
      const isValidOption = this.isValidCombination(size, this.selectedVase)

      // Never disable size options - we want users to be able to switch sizes
      // even if it means they'll need to pick a different vase
      sizeElement.classList.remove("opacity-50", "cursor-not-allowed", "pointer-events-none")

      // Remove strike-through effect
      const sizeText = sizeElement.querySelector(".size-text") || sizeElement
      sizeText.classList.remove("line-through")
    }
  }

  /**
   * Select the first valid size option for the current vase
   */
  selectFirstValidSize() {
    for (const sizeElement of this.sizeOptionsEl) {
      if (!sizeElement.dataset) continue

      const size = sizeElement.dataset.size
      if (this.isValidCombination(size, this.selectedVase)) {
        this.selectedSize = size
        this.updateSizeUI()
        break
      }
    }
  }
}